// BL-2031 — the rebuilt invoices' routes (PRJ-79). The first is the one door
// for every status move; the status menu (CORE-10a) adds its check route here.
// BL-2048 adds the Invoices list (PRJ-80 LIST-1a); BL-2035 the invoice page's read view;
// BL-2046 the assign check and apply (CORE-12); BL-2232 the split check and apply; BL-2050 the list's search,
// filters and counts route (LIST-2); BL-2042 the PDF (CORE-9); BL-2036 the Edit
// stage's draft read, open, change, discard and save (CORE-3); BL-2217 the
// attachments (CORE-12b); BL-2139 Change seller's check and apply (PRJ-81).
import type { ListPayload } from '@auctogon/core/api';
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute, type TenantSource } from './define-admin-route';
// Type-only imports (erased at build) — keep the registry client-bundle-safe.
import type { InvoiceAddLotsCheckDto, InvoiceAddLotsDto } from '../../schemas/responses/invoice-add-lots';
import type { InvoiceSplitCheckDto, InvoiceSplitDto } from '../../schemas/responses/invoice-split';
import type { RecordAssignCheckDto, RecordAssignDto } from '../../schemas/responses/record-assignment';
import type {
  InvoiceAttachmentDto,
  RecordAttachmentArchivedDto,
  RecordAttachmentDownloadDto,
} from '../../schemas/responses/record-attachment';
import type { InvoiceDetailDto } from '../../schemas/responses/invoice-detail';
import type {
  InvoiceDraftDiscardedDto,
  InvoiceDraftDto,
  InvoiceDraftPreviewDto,
  InvoiceDraftReviewDto,
  InvoiceDraftSavedDto,
  InvoiceDraftWriteDto,
} from '../../schemas/responses/invoice-draft';
import type { ChangeLineSellerCheckDto, ChangeLineSellerDto } from '../../schemas/responses/invoice-line-seller';
import type { InvoiceListCountsDto, InvoiceListRowDto } from '../../schemas/responses/invoice-list';
import type { InvoiceSendCheckDto, InvoiceSendDto } from '../../schemas/responses/invoice-send';
import type { InvoiceTransitionCheckDto, InvoiceTransitionDto } from '../../schemas/responses/invoice-transition';

/** An attachment named by `:attachmentId`, which must belong to the `:id` invoice (BL-2217). */
const ATTACHMENT_IN_INVOICE = {
  from: 'record',
  table: 'invoice_attachment',
  param: 'attachmentId',
  within: { column: 'invoiceId', param: 'id' },
} as const satisfies TenantSource;

export const invoices = {
  index: defineAdminRoute({
    path: '/api/invoices',
    params: {},
    methods: {
      // One page of the tenant's invoices, searched, sorted and filtered on the
      // server (ADR 059 — Search Spine). The filters are BL-2050's.
      GET: {
        grant: 'invoice.read',
        tenant: { from: 'input', field: 'tenantId', in: 'query' },
        response: responseType<ListPayload<InvoiceListRowDto>>(),
        query: {
          tenantId: '',
          page: '',
          pageSize: '',
          sortField: '',
          sortDir: '',
          search: '',
          status: '',
          payment: '',
          auction: '',
          assignee: '',
          // BL-2458 — the Sales Rep filter: rep ids and/or House, on the version's copy.
          rep: '',
          created: '',
          created_range: '',
          view: '',
          // BL-2073 — one buyer's invoices (the customer page's Invoices card and its "View all").
          buyer: '',
        },
      },
    },
  }),
  // BL-2050 (LS3) — every filter option's count under the list's other filters
  // and its search; the one counts route the facets (and BL-2051's tiles) read.
  counts: defineAdminRoute({
    path: '/api/invoices/counts',
    params: {},
    methods: {
      GET: {
        grant: 'invoice.read',
        tenant: { from: 'input', field: 'tenantId', in: 'query' },
        response: responseType<InvoiceListCountsDto>(),
        query: {
          tenantId: '',
          search: '',
          status: '',
          payment: '',
          auction: '',
          assignee: '',
          // BL-2458 — the Sales Rep filter: rep ids and/or House, on the version's copy.
          rep: '',
          created: '',
          created_range: '',
          view: '',
          // BL-2073 — one buyer's invoices (the customer page's Invoices card and its "View all").
          buyer: '',
        },
      },
    },
  }),
  // BL-2651 (PRJ-101 TAX-9) — the tax workbook of the invoices the list shows.
  // The body carries the list's own query; the success body is the .xlsx file,
  // so the verb declares no response DTO; failures keep the JSON envelope.
  export_tax_workbook: defineAdminRoute({
    path: '/api/invoices/export-tax-workbook',
    params: {},
    methods: {
      POST: { grant: 'invoice.read', tenant: { from: 'input', field: 'tenantId', in: 'body' } },
    },
  }),
  // BL-2035 — the invoice page's read view. Another house's id is a 404 at the gate.
  detail: defineAdminRoute({
    path: '/api/invoices/:id',
    params: { id: '' },
    methods: {
      GET: {
        grant: 'invoice.read',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDetailDto>(),
      },
    },
  }),
  // BL-2042 — the invoice PDF (CORE-9): the current version, as the page shows
  // it, on US Letter. Gated on the page's own READ grant: printing changes
  // nothing (ADR 062 — Access Grants). The success body is a file
  // (application/pdf), so the verb declares no response DTO; failures keep the
  // JSON envelope (the inventory export's precedent).
  pdf: defineAdminRoute({
    path: '/api/invoices/:id/pdf',
    params: { id: '' },
    methods: {
      GET: { grant: 'invoice.read', tenant: { from: 'record', table: 'invoice', param: 'id' } },
    },
  }),
  transition: defineAdminRoute({
    path: '/api/invoices/transition',
    params: {},
    methods: {
      // The floor every move shares. Void and reopen need invoice.void, every
      // other move invoice.update; the handler narrows to the move's own grant
      // from INVOICE_TRANSITION_CATALOG — declared residue, since the move is in
      // the body (the same shape as `auctions.id.transition`). Every id must be
      // an invoice of one tenant the caller belongs to; another tenant's id is
      // a 404 at the gate. `max` matches INVOICE_TRANSITION_MAX_IDS.
      POST: {
        grant: { anyOf: ['invoice.update', 'invoice.void'] },
        tenant: { from: 'records', table: 'invoice', field: 'invoiceIds', max: 100 },
        response: responseType<InvoiceTransitionDto>(),
      },
    },
  }),
  // BL-2043 (CORE-10a) — the status menu's read-only half: which moves the
  // selection allows, where each lands, its cautions, and Mark as Open's
  // invoice-date prefill (ADR 033 — Transition Service, Decision 9). The floor
  // is the transition route's; the menu gates each row on its own grant with
  // `can()`, and the apply narrows on the server. Every id must be an invoice
  // of one tenant the caller belongs to; another tenant's id is a 404.
  transition_check: defineAdminRoute({
    path: '/api/invoices/transition-check',
    params: {},
    methods: {
      POST: {
        grant: { anyOf: ['invoice.update', 'invoice.void'] },
        tenant: { from: 'records', table: 'invoice', field: 'invoiceIds', max: 100 },
        response: responseType<InvoiceTransitionCheckDto>(),
      },
    },
  }),
  // BL-2046 (CORE-12) — assign, a check then an all-or-nothing apply over
  // `invoiceIds` (ADR 026 — Server Orchestration, Rule 1b; LS6). The invoice
  // page's picker is a bulk of one through both. Every id must be an invoice of
  // one tenant the caller belongs to; another tenant's id is a 404 at the gate.
  // `max` matches RECORD_ASSIGN_MAX_IDS (BL-2525 shares the shape with settlements).
  assign_check: defineAdminRoute({
    path: '/api/invoices/assign-check',
    params: {},
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'records', table: 'invoice', field: 'invoiceIds', max: 100 },
        response: responseType<RecordAssignCheckDto>(),
      },
    },
  }),
  assign: defineAdminRoute({
    path: '/api/invoices/assign',
    params: {},
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'records', table: 'invoice', field: 'invoiceIds', max: 100 },
        response: responseType<RecordAssignDto>(),
      },
    },
  }),
  // BL-2041 (CORE-8) — Send and Resend, a check then an all-or-nothing apply
  // over `invoiceIds` (ADR 026 — Server Orchestration, Rule 1b; LS6). The
  // invoice page's Send is a bulk of one through both, and bulk Send (BL-2052)
  // will use the same pair. Emailing an invoice is `invoice.send` (CR18). Every
  // id must be an invoice of one tenant the caller belongs to; another tenant's
  // id is a 404 at the gate. `max` matches INVOICE_SEND_BATCH_CAP.
  send_check: defineAdminRoute({
    path: '/api/invoices/send-check',
    params: {},
    methods: {
      POST: {
        grant: 'invoice.send',
        tenant: { from: 'records', table: 'invoice', field: 'invoiceIds', max: 50 },
        response: responseType<InvoiceSendCheckDto>(),
      },
    },
  }),
  send: defineAdminRoute({
    path: '/api/invoices/send',
    params: {},
    methods: {
      POST: {
        grant: 'invoice.send',
        tenant: { from: 'records', table: 'invoice', field: 'invoiceIds', max: 50 },
        response: responseType<InvoiceSendDto>(),
      },
    },
  }),
  // BL-2047 (CORE-13) — Add to invoice: a check then an all-or-nothing apply
  // over `lotIds` (ADR 026 — Server Orchestration, Rule 1b; LS6). The lot menu
  // is a bulk of one through both. Creating or adding is `invoice.create`
  // (CR18; packages/audit/src/access-alignment.ts). Every id must be a lot of
  // one tenant the caller belongs to; another tenant's lot is a 404 at the gate.
  // `max` matches ADD_TO_INVOICE_MAX_LOTS.
  add_lots_check: defineAdminRoute({
    path: '/api/invoices/add-lots-check',
    params: {},
    methods: {
      POST: {
        grant: 'invoice.create',
        tenant: { from: 'records', table: 'lot', field: 'lotIds', max: 50 },
        response: responseType<InvoiceAddLotsCheckDto>(),
      },
    },
  }),
  add_lots: defineAdminRoute({
    path: '/api/invoices/add-lots',
    params: {},
    methods: {
      POST: {
        grant: 'invoice.create',
        tenant: { from: 'records', table: 'lot', field: 'lotIds', max: 50 },
        response: responseType<InvoiceAddLotsDto>(),
      },
    },
  }),
  // BL-2232 (PRJ-79 FIX-4) — Split to a new invoice: a read-only check that
  // prices both sides, and the all-or-nothing apply. `invoice.update`: a split
  // edits the original (both grants map to INVOICES_MANAGE today). Another
  // house's id is a 404 at the gate.
  split_check: defineAdminRoute({
    path: '/api/invoices/:id/split-check',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceSplitCheckDto>(),
      },
    },
  }),
  split: defineAdminRoute({
    path: '/api/invoices/:id/split',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceSplitDto>(),
      },
    },
  }),
  // BL-2036 (CORE-3) — the Edit stage: the issued invoice's shared draft. Every
  // write names the draft id and revision it was made from and is refused
  // (409 DRAFT_STALE) when the draft moved on. Another house's id is a 404 at the gate.
  // BUG-2151: DELETE (Discard), like draft_restore, moves a thrown-away Change
  // seller's settlement back under `invoice.update` alone, the same dual-grant
  // footing as change_seller below. BUG-2448: DELETE also swaps back the lots a
  // buyer swap took off the draft (bids and winner, which /api/lots/swap-buyer
  // guards with `lot.correct_sale`) under `invoice.update` alone. Both grants map
  // to the same permission (pinned by grants.test.ts); if they split, add the narrower check here.
  draft: defineAdminRoute({
    path: '/api/invoices/:id/draft',
    params: { id: '' },
    methods: {
      GET: {
        grant: 'invoice.read',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDraftDto>(),
      },
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDraftWriteDto>(),
      },
      PATCH: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDraftWriteDto>(),
      },
      DELETE: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDraftDiscardedDto>(),
      },
    },
  }),
  // BL-2037 (CORE-4) — the Add adjustment preview: the draft priced with the
  // page's unsaved changes and the change being made. Read-only (nothing is
  // written), but only an editor previews an edit, so it carries the write grant.
  draft_preview: defineAdminRoute({
    path: '/api/invoices/:id/draft/preview',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDraftPreviewDto>(),
      },
    },
  }),
  // BL-2256 (CORE-8) — restore an older live or superseded version into a new
  // draft, replacing the open draft only when the body names the copy the caller saw.
  draft_restore: defineAdminRoute({
    path: '/api/invoices/:id/draft/restore',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDraftWriteDto>(),
      },
    },
  }),
  // BL-2227 — "Review your changes" before an issued invoice's draft is saved:
  // every change against the live version and the totals before → after. A
  // read (nothing is written), but only an editor reviews a save, so it carries
  // the write grant, like the preview.
  draft_review: defineAdminRoute({
    path: '/api/invoices/:id/draft/review',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDraftReviewDto>(),
      },
    },
  }),
  draft_save: defineAdminRoute({
    path: '/api/invoices/:id/draft/save',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceDraftSavedDto>(),
      },
    },
  }),
  // BL-2217 (CORE-12b) — files attached to an invoice, Staff only or Share
  // with buyer. Register gates on the invoice; the three `:attachmentId` verbs
  // gate on the attachment itself, within its invoice, so another invoice's or
  // house's attachment — or a removed one — is the gate's 404.
  attachments: defineAdminRoute({
    path: '/api/invoices/:id/attachments',
    params: { id: '' },
    methods: {
      POST: {
        grant: 'invoice.update',
        tenant: { from: 'record', table: 'invoice', param: 'id' },
        response: responseType<InvoiceAttachmentDto>(),
      },
    },
  }),
  attachment: defineAdminRoute({
    path: '/api/invoices/:id/attachments/:attachmentId',
    params: { id: '', attachmentId: '' },
    methods: {
      PATCH: { grant: 'invoice.update', tenant: ATTACHMENT_IN_INVOICE, response: responseType<InvoiceAttachmentDto>() },
      DELETE: { grant: 'invoice.update', tenant: ATTACHMENT_IN_INVOICE, response: responseType<RecordAttachmentArchivedDto>() },
    },
  }),
  // BL-2139 (PRJ-81) — Change seller on invoiced lots: a read-only check and an
  // all-or-nothing apply into the draft (ADR 026 Rule 1b); the row action is a
  // bulk of one. No lot, item or customer is written. BL-2563: the apply also
  // moves each lot to the new seller's settlement, under `invoice.update` alone
  // (both it and `settlement.update` map to roles Admin and Accounting hold
  // today). A role holding one but not the other needs a per-field check first.
  change_seller_check: defineAdminRoute({
    path: '/api/invoices/:id/change-seller-check',
    params: { id: '' },
    methods: {
      POST: { grant: 'invoice.update', tenant: { from: 'record', table: 'invoice', param: 'id' }, response: responseType<ChangeLineSellerCheckDto>() },
    },
  }),
  change_seller: defineAdminRoute({
    path: '/api/invoices/:id/change-seller',
    params: { id: '' },
    methods: {
      POST: { grant: 'invoice.update', tenant: { from: 'record', table: 'invoice', param: 'id' }, response: responseType<ChangeLineSellerDto>() },
    },
  }),
  // BL-2425 — Remove from invoice outside the Edit stage: the lots come off the
  // version the page showed, into the open draft (opened when an issued invoice
  // has none) or a Preparing invoice's v1, in one transaction (ADR 026). The row
  // ⋮ item is a bulk of one; the Edit stage removes through PATCH draft.
  remove_lots: defineAdminRoute({
    path: '/api/invoices/:id/remove-lots',
    params: { id: '' },
    methods: {
      POST: { grant: 'invoice.update', tenant: { from: 'record', table: 'invoice', param: 'id' }, response: responseType<InvoiceDraftWriteDto>() },
    },
  }),
  attachment_download: defineAdminRoute({
    path: '/api/invoices/:id/attachments/:attachmentId/download',
    params: { id: '', attachmentId: '' },
    methods: {
      GET: { grant: 'invoice.read', tenant: ATTACHMENT_IN_INVOICE, response: responseType<RecordAttachmentDownloadDto>() },
    },
  }),
};
